const { REACT_APP_NAME } = process.env;

import {
    Box,
    Typography,
    Skeleton,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    CircularProgress,
    useTheme,
    useMediaQuery,
    Switch,
    FormControlLabel,
} from '@mui/material';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageContainer from 'src/components/container/PageContainer';
import { usePostBodyWithJwtMutation } from 'src/redux/api/cabinet';
import { IconSearch } from '@tabler/icons-react';

import { CurrencyType, Program, ChainUser, Level } from './types';
import { DepthSelector } from './DepthSelector';
import { ChainUserCard } from './ChainUserCard';

// ─── Helpers ──────────────────────────────────────────────────────────────

const normalizePrograms = (programs: Program[]): Program[] =>
    programs.map((p) => ({
        ...p,
        currency: p.currency?.toLowerCase() || 'usdt',
    }));

// ─── Component ────────────────────────────────────────────────────────────

export const MarketingAnaliticsPageNew = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const primaryColor = theme.palette.primary.main;

    const [searchParams, setSearchParams] = useSearchParams();

    // ── Nav state ──
    const [selectedCurrency, setSelectedCurrency] = useState<CurrencyType>('usdt');
    const [selectedProgramTab, setSelectedProgramTab] = useState(0);
    const [selectedLevelTab, setSelectedLevelTab] = useState(0);
    const [selectedDepth, setSelectedDepth] = useState(1);
    const [programs, setPrograms] = useState<Program[]>([]);
    const [valueLogin, setValueLogin] = useState('');
    const [isInitialized, setIsInitialized] = useState(false);
    const [isVirtual, setIsVirtual] = useState(false);

    // ── Pagination state ──
    const [chainUsers, setChainUsers] = useState<ChainUser[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [total, setTotal] = useState(0);
    const [serverMessage, setServerMessage] = useState('Нет данных для отображения');

    // ── Loading flags — separate for initial vs "load more" ──
    const [isLoadingInitial, setIsLoadingInitial] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const jwt = localStorage.getItem('jwt') || '';
    const [postBodyWithJwt] = usePostBodyWithJwtMutation();

    // ── Derived ──
    const filteredPrograms = useMemo(
        () => programs.filter((p) => (p.currency || 'usdt') === selectedCurrency),
        [programs, selectedCurrency],
    );

    const currentProgram = filteredPrograms[selectedProgramTab] ?? null;

    // ── URL sync ──
    const updateURLParams = useCallback(
        (currency: CurrencyType, programIndex: number, levelIndex: number, depth: number, login: string, virtual: boolean) => {
            const params: Record<string, string> = { currency, depth: depth.toString(), virtual: virtual.toString() };
            const prg = filteredPrograms[programIndex];
            if (prg) params.program = prg.id.toString();
            if (prg?.levels?.[levelIndex]) params.level = prg.levels[levelIndex].level.toString();
            if (login) params.login = login;
            setSearchParams(params);
        },
        [filteredPrograms, setSearchParams],
    );

    // ── Core fetch — page=1 resets list, page>1 appends ──
    const fetchPage = useCallback(
        async (
            programId: number,
            level: number,
            depth: number,
            login: string,
            page: number,
            virtual: boolean,
        ) => {
            const isFirst = page === 1;
            if (isFirst) setIsLoadingInitial(true);
            else setIsLoadingMore(true);

            try {
                const response = await postBodyWithJwt({
                    route: 'marketing-analytics-test',
                    body: { program: programId, level, depth, login, page, virtual },
                    jwt,
                }).unwrap();

                setServerMessage(response?.message || 'Нет данных для отображения');
                setHasMore(response?.has_more ?? false);
                setTotal(response?.total ?? 0);
                setCurrentPage(response?.page ?? page);

                // programs only arrive on page=1 — preserve on subsequent pages
                if (response?.programs) {
                    setPrograms(normalizePrograms(response.programs));
                }

                const newData: ChainUser[] = response?.data ?? [];
                if (isFirst) {
                    setChainUsers(newData);
                } else {
                    setChainUsers((prev) => [...prev, ...newData]);
                }
            } catch (err) {
                console.error('Failed to fetch chain data:', err);
            } finally {
                if (isFirst) setIsLoadingInitial(false);
                else setIsLoadingMore(false);
            }
        },
        [postBodyWithJwt, jwt],
    );

    // ── Reset & reload helpers ──
    const reloadFromPage1 = useCallback(
        (programId: number, level: number, depth: number, login: string, virtual: boolean) => {
            setCurrentPage(1);
            setChainUsers([]);
            fetchPage(programId, level, depth, login, 1, virtual);
        },
        [fetchPage],
    );

    const handleLoadMore = useCallback(() => {
        const prg = filteredPrograms[selectedProgramTab];
        if (!prg?.levels?.[selectedLevelTab]) return;
        const nextPage = currentPage + 1;
        fetchPage(prg.id, Number(prg.levels[selectedLevelTab].level), selectedDepth, valueLogin, nextPage, isVirtual);
    }, [filteredPrograms, selectedProgramTab, selectedLevelTab, selectedDepth, valueLogin, currentPage, fetchPage, isVirtual]);

    // ── Currency / program / level / depth change — always reset to page 1 ──
    const handleCurrencyChange = useCallback(
        (newCurrency: CurrencyType) => {
            setSelectedCurrency(newCurrency);
            setSelectedProgramTab(0);
            setSelectedLevelTab(0);
            const currPrograms = programs.filter((p) => (p.currency || 'usdt') === newCurrency);
            if (currPrograms.length > 0 && currPrograms[0].levels?.length > 0) {
                const prg = currPrograms[0];
                reloadFromPage1(prg.id, Number(prg.levels[0].level), selectedDepth, valueLogin, isVirtual);
                updateURLParams(newCurrency, 0, 0, selectedDepth, valueLogin, isVirtual);
            }
        },
        [programs, selectedDepth, valueLogin, reloadFromPage1, updateURLParams, isVirtual],
    );

    const handleProgramTabChange = useCallback(
        (newValue: number, keepLevel = false) => {
            setSelectedProgramTab(newValue);
            const lvlIdx = keepLevel ? selectedLevelTab : 0;
            if (!keepLevel) setSelectedLevelTab(0);
            const prg = filteredPrograms[newValue];
            if (prg?.levels?.length) {
                reloadFromPage1(prg.id, Number(prg.levels[lvlIdx].level), selectedDepth, valueLogin, isVirtual);
                updateURLParams(selectedCurrency, newValue, lvlIdx, selectedDepth, valueLogin, isVirtual);
            }
        },
        [filteredPrograms, selectedLevelTab, selectedCurrency, selectedDepth, valueLogin, reloadFromPage1, updateURLParams, isVirtual],
    );

    const handleLevelTabChange = useCallback(
        (newValue: number) => {
            setSelectedLevelTab(newValue);
            const prg = filteredPrograms[selectedProgramTab];
            if (prg?.levels?.[newValue]) {
                reloadFromPage1(prg.id, Number(prg.levels[newValue].level), selectedDepth, valueLogin, isVirtual);
                updateURLParams(selectedCurrency, selectedProgramTab, newValue, selectedDepth, valueLogin, isVirtual);
            }
        },
        [filteredPrograms, selectedProgramTab, selectedCurrency, selectedDepth, valueLogin, reloadFromPage1, updateURLParams, isVirtual],
    );

    const handleDepthChange = useCallback(
        (depth: number) => {
            setSelectedDepth(depth);
            const prg = filteredPrograms[selectedProgramTab];
            if (prg?.levels?.[selectedLevelTab]) {
                reloadFromPage1(prg.id, Number(prg.levels[selectedLevelTab].level), depth, valueLogin, isVirtual);
                updateURLParams(selectedCurrency, selectedProgramTab, selectedLevelTab, depth, valueLogin, isVirtual);
            }
        },
        [filteredPrograms, selectedProgramTab, selectedLevelTab, selectedCurrency, valueLogin, reloadFromPage1, updateURLParams, isVirtual],
    );

    const handleSubmit = useCallback(() => {
        const prg = filteredPrograms[selectedProgramTab];
        if (!prg?.levels?.[selectedLevelTab]) return;
        reloadFromPage1(prg.id, Number(prg.levels[selectedLevelTab].level), selectedDepth, valueLogin.trim(), isVirtual);
        updateURLParams(selectedCurrency, selectedProgramTab, selectedLevelTab, selectedDepth, valueLogin.trim(), isVirtual);
    }, [filteredPrograms, selectedProgramTab, selectedLevelTab, selectedCurrency, selectedDepth, valueLogin, reloadFromPage1, updateURLParams, isVirtual]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); } },
        [handleSubmit],
    );

    const handleVirtualChange = useCallback(
        (newVirtual: boolean) => {
            setIsVirtual(newVirtual);
            const prg = filteredPrograms[selectedProgramTab];
            if (prg?.levels?.[selectedLevelTab]) {
                reloadFromPage1(prg.id, Number(prg.levels[selectedLevelTab].level), selectedDepth, valueLogin, newVirtual);
                updateURLParams(selectedCurrency, selectedProgramTab, selectedLevelTab, selectedDepth, valueLogin, newVirtual);
            }
        },
        [filteredPrograms, selectedProgramTab, selectedLevelTab, selectedCurrency, selectedDepth, valueLogin, reloadFromPage1, updateURLParams],
    );

    // ── Initial load from URL params ──
    const fetchInitialData = useCallback(async () => {
        setIsLoadingInitial(true);
        try {
            const urlCurrency = searchParams.get('currency') as CurrencyType;
            const validCurrency: CurrencyType = urlCurrency === 'btc' || urlCurrency === 'usdt' ? urlCurrency : 'usdt';
            const urlProgramId = searchParams.get('program');
            const urlLevel = searchParams.get('level');
            const urlLogin = searchParams.get('login');
            const urlDepth = searchParams.get('depth');
            const urlVirtual = searchParams.get('virtual');

            if (urlLogin) setValueLogin(urlLogin);
            const depth = urlDepth ? Math.min(10, Math.max(1, parseInt(urlDepth) || 1)) : 1;
            if (urlDepth) setSelectedDepth(depth);
            const virtual = urlVirtual === 'true';
            if (urlVirtual !== null) setIsVirtual(virtual);
            setSelectedCurrency(validCurrency);

            // First call to get programs list (page=1, any valid program)
            const bootstrapResponse = await postBodyWithJwt({
                route: 'marketing-analytics-test?program=7&level=6',
                body: {},
                jwt,
            }).unwrap();

            if (!bootstrapResponse.programs) return;

            const normalized = normalizePrograms(bootstrapResponse.programs);
            setPrograms(normalized);

            const currPrograms = normalized.filter((p) => p.currency === validCurrency);
            let progIdx = 0;
            let lvlIdx = 0;

            if (urlProgramId) {
                const found = currPrograms.findIndex((p) => p.id === parseInt(urlProgramId));
                if (found !== -1) progIdx = found;
            } else {
                const active = currPrograms.findIndex((p) => p.status === true);
                if (active !== -1) progIdx = active;
            }

            setSelectedProgramTab(progIdx);
            const activeProgram = currPrograms[progIdx];

            if (activeProgram?.levels) {
                if (urlLevel) {
                    const found = activeProgram.levels.findIndex((l: Level) => l.level === parseFloat(urlLevel));
                    if (found !== -1) lvlIdx = found;
                } else {
                    const active = activeProgram.levels.findIndex((l: Level) => l.is_current);
                    if (active !== -1) lvlIdx = active;
                }
                setSelectedLevelTab(lvlIdx);
            }

            updateURLParams(validCurrency, progIdx, lvlIdx, depth, urlLogin || '', virtual);
            setIsInitialized(true);

            // Now fetch page 1 of the actual selected program/level
            if (activeProgram?.levels?.[lvlIdx]) {
                const response = await postBodyWithJwt({
                    route: 'marketing-analytics-test',
                    body: {
                        program: activeProgram.id,
                        level: Number(activeProgram.levels[lvlIdx].level),
                        depth,
                        login: urlLogin || '',
                        page: 1,
                        virtual: virtual,
                    },
                    jwt,
                }).unwrap();

                setServerMessage(response?.message || 'Нет данных для отображения');
                setHasMore(response?.has_more ?? false);
                setTotal(response?.total ?? 0);
                setCurrentPage(1);
                setChainUsers(response?.data ?? []);
            }
        } catch (err) {
            console.error('Failed initial load:', err);
            setIsInitialized(true);
        } finally {
            setIsLoadingInitial(false);
        }
    }, [jwt, searchParams, postBodyWithJwt, updateURLParams]);

    useEffect(() => {
        if (jwt && !isInitialized) fetchInitialData();
    }, [jwt, isInitialized, fetchInitialData]);

    // ─── Button style ──────────────────────────────────────────────────────
    const tabBtnStyle = (active: boolean, size: 'sm' | 'md' = 'md'): React.CSSProperties => ({
        cursor: 'pointer',
        backgroundColor: active ? primaryColor : 'transparent',
        borderRadius: 6,
        padding: size === 'sm'
            ? (isMobile ? '4px 10px' : '6px 12px')
            : (isMobile ? '6px 12px' : '8px 16px'),
        fontSize: size === 'sm' ? (isMobile ? 14 : 14) : (isMobile ? 14 : 14),
        color: active ? '#fff' : primaryColor,
        fontWeight: 'bold',
        border: `1px solid ${active ? primaryColor : `${primaryColor}40`}`,
        transition: 'all 0.15s',
    });

    const isAnyLoading = isLoadingInitial || isLoadingMore;

    // ─── Render ───────────────────────────────────────────────────────────
    return (
        <PageContainer title={`${REACT_APP_NAME} | Анализ цепочек`} description="Анализ цепочек">
            <>
                {/* ── Title + search ── */}
                <Box mb={2} display="flex" alignItems="center" flexWrap="wrap" justifyContent="space-between" gap={2}>
                    <Box>
                        <Typography variant="h4">Анализ цепочек</Typography>
                        <Typography variant="body2" sx={{ color: 'grey.400' }}>
                            Главная » Структура » Анализ цепочек
                        </Typography>
                    </Box>
                    <Box sx={{ minWidth: isMobile ? '100%' : 250 }}>
                        <TextField
                            fullWidth size="small"
                            value={valueLogin}
                            onChange={(e) => setValueLogin(e.target.value)}
                            placeholder="Логин пользователя"
                            onKeyDown={handleKeyDown}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton sx={{ background: 'white' }} onClick={handleSubmit} edge="end">
                                            <IconSearch />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </Box>

                {/* ── Currency ── */}
                <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400, mb: 1 }}>Валюта</Typography>
                <Box mb={2} display="flex" gap={1} flexWrap="wrap">
                    {(['usdt', 'btc'] as CurrencyType[]).map((c) => (
                        <button key={c} onClick={() => handleCurrencyChange(c)} style={tabBtnStyle(selectedCurrency === c)}>
                            {c === 'usdt' ? 'USDT' : 'Bitcoin (BY)'}
                        </button>
                    ))}
                </Box>

                {/* ── Programs ── */}
                <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400, mb: 1 }}>Платформы</Typography>
                <Box mb={2}>
                    {isLoadingInitial && !isInitialized ? (
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {[...Array(8)].map((_, i) => (
                                <Skeleton key={i} variant="rounded" width={80} height={38} sx={{ borderRadius: '6px' }} />
                            ))}
                        </Box>
                    ) : filteredPrograms.length > 0 ? (
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {filteredPrograms.map((p, idx) => (
                                <button
                                    key={`prog-${p.id}-${idx}`}
                                    onClick={() => handleProgramTabChange(idx)}
                                    style={tabBtnStyle(selectedProgramTab === idx)}
                                    dangerouslySetInnerHTML={{ __html: p.name }}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography sx={{ color: 'grey.400' }}>Нет программ для выбранной валюты</Typography>
                    )}
                </Box>

                {/* ── Levels ── */}
                {currentProgram?.levels?.length > 0 && (
                    <>
                        <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400, mb: 1 }}>Уровни</Typography>
                        <Box mb={2}>
                            {isLoadingInitial && !isInitialized ? (
                                <Box display="flex" gap={1} flexWrap="wrap">
                                    {[...Array(4)].map((_, i) => (
                                        <Skeleton key={i} variant="rounded" width={80} height={34} sx={{ borderRadius: '6px' }} />
                                    ))}
                                </Box>
                            ) : (
                                <Box display="flex" flexWrap="wrap" gap={0.5}>
                                    {currentProgram.levels.map((lvl, idx) => (
                                        <button
                                            key={`lvl-${currentProgram.id}-${idx}`}
                                            onClick={() => handleLevelTabChange(idx)}
                                            style={tabBtnStyle(selectedLevelTab === idx, 'sm')}
                                            dangerouslySetInnerHTML={{
                                                __html: `${lvl.name} - ${currentProgram.currency?.toLowerCase() === 'btc' ? 'BTC' : '$'}${lvl.level}`,
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </>
                )}

                {/* ── Depth ── */}
                <Box mb={3}>
                    <DepthSelector value={selectedDepth} onChange={handleDepthChange} disabled={isAnyLoading} />
                </Box>

                {/* ── Virtual chains ── */}
                <Box mb={2}>
                    <button
                        onClick={() => handleVirtualChange(!isVirtual)}
                        disabled={isAnyLoading}
                        style={{
                            ...tabBtnStyle(isVirtual),
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            opacity: isAnyLoading ? 0.5 : 1,
                            cursor: isAnyLoading ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {/* Кастомный трек */}
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            width: 36,
                            height: 20,
                            borderRadius: 10,
                            backgroundColor: isVirtual ? '#fff' : `${primaryColor}30`,
                            border: `2px solid ${isVirtual ? '#fff' : `${primaryColor}60`}`,
                            padding: '2px',
                            transition: 'all 0.2s',
                            flexShrink: 0,
                        }}>
                            <span style={{
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                backgroundColor: isVirtual ? primaryColor : `${primaryColor}90`,
                                transform: isVirtual ? 'translateX(16px)' : 'translateX(0)',
                                transition: 'transform 0.2s, background-color 0.2s',
                                flexShrink: 0,
                            }} />
                        </span>
                        Создание цепей
                    </button>
                </Box>

                {/* ── Chain rows ── */}
                {isLoadingInitial ? (
                    <Box display="flex" flexDirection="column" gap={3}>
                        {[...Array(3)].map((_, i) => (
                            <Box key={i}>
                                <Skeleton variant="text" width={160} height={28} sx={{ mb: 1 }} />
                                <Box display="flex" gap={1.5}>
                                    {[...Array(4)].map((_, j) => (
                                        <Skeleton key={j} variant="rounded" width={220} height={280} sx={{ borderRadius: 2, flexShrink: 0 }} />
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : chainUsers.length > 0 ? (
                    <>
                        {/* Counter */}
                        <Typography variant="caption" color="text.disabled" sx={{ mb: 1.5, display: 'block' }}>
                            Показано {chainUsers.length} из {total} цепочек
                        </Typography>

                        <Box display="flex"
                            flexDirection="column"
                            sx={{
                                minWidth: '200px',
                                overflowX: 'hidden',

                            }}>


                            {chainUsers.map((user, idx) => (
                                <ChainUserCard
                                    key={`chain-${user.user_id}-${idx}`}
                                    user={user}
                                    currency={currentProgram?.currency ?? 'usdt'}
                                    level={currentProgram.levels[selectedLevelTab].level}
                                    index={idx}
                                />
                            ))}
                        </Box>

                        {/* Load more */}
                        {hasMore && (
                            <Box display="flex" justifyContent="center" mt={3} mb={2}>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={handleLoadMore}
                                    disabled={isLoadingMore}
                                    startIcon={isLoadingMore ? <CircularProgress size={18} color="inherit" /> : null}
                                    sx={{
                                        minWidth: 220,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderColor: primaryColor,
                                        color: primaryColor,
                                        '&:hover': { borderColor: primaryColor, bgcolor: `${primaryColor}12` },
                                        '&.Mui-disabled': { borderColor: 'divider' },
                                    }}
                                >
                                    {isLoadingMore ? 'Загружаем...' : `Показать ещё (${total - chainUsers.length})`}
                                </Button>
                            </Box>
                        )}

                        {/* All loaded */}
                        {!hasMore && total > 0 && (
                            <Box textAlign="center" mt={2} mb={1}>
                                <Typography variant="caption" color="text.disabled">
                                    Все {total} цепочек загружены
                                </Typography>
                            </Box>
                        )}
                    </>
                ) : !isLoadingInitial ? (
                    <Box textAlign="center" py={6}>
                        <Typography variant="h5" sx={{ color: 'grey.400' }}>{serverMessage}</Typography>
                    </Box>
                ) : null}
            </>
        </PageContainer>
    );
};